import type { HTMLAttributes } from 'react';
import { cx } from '../lib/cx';

export interface SparklineProps extends HTMLAttributes<HTMLDivElement> {
  /** Series values; bars scale to the max. */
  data: number[];
  height?: number;
  /** Accessible summary, e.g. "Bookings, last 6 days". */
  label?: string;
  /** One short caption per bar (e.g. weekday initials). Same length as `data`. */
  labels?: string[];
  /** Index to emphasise — "today" in a 7-day call-volume strip. */
  highlightIndex?: number;
}

/** Tiny bar sparkline using the sequential green ramp (brief §5 / §7.5).
 *  Colour is reinforced by height — never meaning-by-colour-alone. */
export function Sparkline({ data, height = 28, label, labels, highlightIndex, className, style, ...rest }: SparklineProps) {
  const max = Math.max(1, ...data);
  return (
    <div
      className={cx('tc-sparkline', labels && 'tc-sparkline--labelled', className)}
      style={{ height, ...style }}
      role="img"
      aria-label={label ?? `Sparkline of ${data.length} values`}
      {...rest}
    >
      {data.map((v, i) => {
        const pct = Math.max(6, Math.round((v / max) * 100));
        // deeper green for taller bars
        const step = Math.min(7, Math.max(2, Math.ceil((pct / 100) * 6) + 1));
        const bar = (
          <span
            className="tc-sparkline__bar"
            style={{ height: `${pct}%`, background: `var(--tc-seq-${step})` }}
          />
        );
        if (!labels) return <span key={i} className="tc-sparkline__slot">{bar}</span>;
        return (
          <span key={i} className={cx('tc-sparkline__slot', i === highlightIndex && 'tc-sparkline__slot--current')}>
            {bar}
            <span className="tc-sparkline__caption">{labels[i]}</span>
          </span>
        );
      })}
    </div>
  );
}
